import { makeAutoObservable } from "mobx";
import { IShareVoucherRequest, IVoucher, VoucherDiscountType } from ".";
import { ApiResponse, IRootStore } from "..";
import Api from "../../services/api";
import { ApiConfig } from "../../services/api-config";
import _ from "lodash";
import moment from "moment";
import { ShowAlert } from "../../utils/functions";

class VoucherStore {
  vouchers: Array<IVoucher> = [];
  liveVouchers: Array<IVoucher> = [];
  discountTypes: Array<VoucherDiscountType> = [];
  editVoucher: IVoucher | null = null;
  previewEmailBody: string | null = null;

  private rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  fetchVouchers = () => {
    const url = ApiConfig.REST_URIS.voucher.allVouchers;
    Api.get(url, (res: ApiResponse<IVoucher[]>) => {
      if (res?.isSuccess && res.data) {
        this.vouchers = _.sortBy(res.data, ["id"]);
      }
    });
  };

  fetchLiveVouchers = () => {
    const url = ApiConfig.REST_URIS.voucher.liveVouchers;
    Api.get(url, (res: ApiResponse<IVoucher[]>) => {
      if (res?.isSuccess && res.data) {
        this.liveVouchers = _.sortBy(res.data, ["id"]);
      }
    });
  };

  setEditVoucher = (offer: IVoucher | null) => {
    this.editVoucher = offer;
  };

  saveVoucher = (offer: IVoucher, callback: Function) => {
    offer.startDate = moment(offer.startDate).format();
    offer.endDate = moment(offer.endDate).format();
    if (offer.voucherId) {
      Api.put(
        ApiConfig.REST_URIS.voucher.updateVoucher(offer.voucherId),
        offer,
        (res: ApiResponse<number>) => {
          callback && callback(res);
        }
      );
    } else {
      Api.post(
        ApiConfig.REST_URIS.voucher.createVoucher,
        offer,
        (res: ApiResponse<number>) => {
          callback && callback(res);
        }
      );
    }
  };

  deleteVoucher = (id: number, callback: Function) => {
    const url = ApiConfig.REST_URIS.voucher.deleteVoucher(id);
    Api.delete(url, (res: ApiResponse<number>) => {
      callback && callback(res?.isSuccess && res.data);
    });
  };

  receiveNewVouchers = (offer: IVoucher) => {
    const index = this.liveVouchers.findIndex(
      (o) => o.voucherId === offer.voucherId
    );
    if (index > -1) {
      this.liveVouchers.splice(index, 1, offer);
    } else {
      this.liveVouchers.unshift(offer);
    }

    this.liveVouchers = [...this.liveVouchers];
  };

  applyVoucher = (code: string, callback: Function) => {
    const url = ApiConfig.REST_URIS.voucher.applyVoucher(code);
    Api.get(url, (res: ApiResponse<IVoucher>) => {
      callback && callback(res);
    });
  };

  shareVoucherWithCustomers = (
    request: IShareVoucherRequest,
    callback: Function
  ) => {
    Api.post(
      ApiConfig.REST_URIS.voucher.shareVoucher(request.voucherId),
      request,
      (res: ApiResponse<boolean>) => {
        callback && callback(res);
      }
    );
  };

  shareVoucherWithAdmin = (voucherId: number, callback: Function) => {
    Api.post(
      ApiConfig.REST_URIS.voucher.shareVoucherWithAdmin(voucherId),
      {},
      (res: ApiResponse<boolean>) => {
        callback && callback(res, true);
      }
    );
  };

  previewVoucherEmail = (voucherId: number) => {
    Api.get(
      ApiConfig.REST_URIS.voucher.previewVoucherEmail(voucherId),
      (res: ApiResponse<string>) => {
        if (res && res.isSuccess) {
          this.previewEmailBody = res.data;
        } else {
          ShowAlert.Error(res?.message ?? "Request failed. Please try again.");
        }
      }
    );
  };

  setPreviewEmailBody = (emailBody: string | null) => {
    this.previewEmailBody = emailBody;
  };
}

export default VoucherStore;
